import { useCallback, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { UsuarioContext } from "../../../providers/userContext";
import useChatEliteSocket from "../../../hooks/useChatEliteSocket";
import { getResumenChatElite } from "../../../services/chatElite.service";
import { setResumenChatEliteStaff } from "../../../hooks/useResumenChatEliteStaff";
import { avisarUnaVez } from "../../../utilities/chatEliteAvisos";

const ROLES_PANEL = ["ADMIN", "CONSUL", "VENT"];
const ROLES_GESTION = ["ADMIN", "CONSUL"];
const DEBOUNCE_MS = 400;

/**
 * Avisos del Chat Elite para el staff en toda la aplicacion (no pinta nada):
 *  - mantiene el resumen (badge del menu) al dia en tiempo real
 *  - sonido si entra una consulta nueva sin asignar o un mensaje en uno de MIS tickets
 *    (una sola vez aunque haya varias pestañas)
 *  - toast si otra persona me asigna un ticket (solo en la pestaña visible)
 */
const Avisos = ({ user }) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const gestiona = ROLES_GESTION.includes(user.rol);
  const { socket } = useChatEliteSocket({ activo: true });
  const previo = useRef(null);
  const timer = useRef(null);

  const cargar = useCallback(async (claveAviso = null) => {
    try {
      const r = await getResumenChatElite();
      const antes = previo.current;
      previo.current = r;
      setResumenChatEliteStaff(r);
      if (gestiona && antes && claveAviso) {
        const nuevaSinAsignar = (r.sinAsignar || 0) > (antes.sinAsignar || 0);
        const nuevoEnMios = (r.noLeidosMios || 0) > (antes.noLeidosMios || 0);
        if (nuevaSinAsignar || nuevoEnMios) avisarUnaVez(claveAviso);
      }
    } catch (_) {}
  }, [gestiona]);

  const cargarDiferido = useCallback((claveAviso) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => cargar(claveAviso), DEBOUNCE_MS);
  }, [cargar]);

  useEffect(() => {
    cargar();
    return () => clearTimeout(timer.current);
  }, [cargar]);

  useEffect(() => {
    if (!socket) return undefined;
    const onTicket = (d) => {
      cargarDiferido(d.tipo === "creado" ? `t-${d.ticketId}` : null);
      if (d.asignadoA && d.asignadoA === user.user_id && d.asignadoPor && d.asignadoPor !== user.user_id
        && document.visibilityState === "visible") {
        toast.info(t("chatElite.panel.teHanAsignado", { id: d.ticketId }), {
          toastId: `ce-asignado-${d.ticketId}`,
          onClick: () => navigate(`/chat-elite?ticket=${d.ticketId}`),
        });
      }
    };
    const onMensaje = (d) => cargarDiferido(d.interno ? null : `m-${d.mensajeId}`);
    const onLeido = (d) => {
      if (d.userId === user.user_id) cargarDiferido(null); // lo he leido yo (quiza en otra pestaña)
    };
    const onConnect = () => cargar();
    socket.on("chatElite:ticket", onTicket);
    socket.on("chatElite:mensaje", onMensaje);
    socket.on("chatElite:leido", onLeido);
    socket.on("connect", onConnect);
    return () => {
      socket.off("chatElite:ticket", onTicket);
      socket.off("chatElite:mensaje", onMensaje);
      socket.off("chatElite:leido", onLeido);
      socket.off("connect", onConnect);
    };
  }, [socket, user.user_id, cargar, cargarDiferido, navigate, t]);

  return null;
};

const AvisosChatEliteStaff = () => {
  const user = useContext(UsuarioContext);
  if (!ROLES_PANEL.includes(user?.rol)) return null;
  return <Avisos user={user} />;
};

export default AvisosChatEliteStaff;
